import { Component, Input, OnInit } from '@angular/core';
import { CommonMessages } from '@app/constants/common.messages';
import { AssessmentGroupInfoFiltered } from '@app/models/evaluation/assessment-group-info.model';
import { EvaluationCycle } from '@app/models/evaluation/evaluation-cycle.model';
import { EvaluationFeedbackRequestState } from '@app/models/evaluation/evaluation-feedback-request-state.model';
import { EvaluationFeedbackRequestType } from '@app/models/evaluation/evaluation-feedback-request-type.model';
import { EvaluationFeedbackRequest } from '@app/models/evaluation/evaluation-feedback-request.model';
import { EvaluationModuleType } from '@app/models/evaluation/evaluation-module-type.model';
import { ReviewStepSummary } from '@app/models/evaluation/peer-review-summary/review-step-summary.model';
import { ReviewSubjectAccessType } from '@app/models/evaluation/review-subject-access-type.model';
import { IState } from '@app/models/state/state.model';
import { SurveyQuestionType } from '@app/models/survey-question-type.enum';
import { UserMinimal } from '@app/models/user/user-minimal.model';
import { EvaluationCycleAPIService } from '@app/shared/api/evaluation-cycle.api.service';
import { UserAPIService } from '@app/shared/api/user.api.service';
import { Globals } from '@app/shared/globals/globals';
import { forkJoin, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-information-sidebar-reviews-upward-reviews',
  templateUrl: './information-sidebar-reviews-upward-reviews.component.html',
  styleUrls: ['./information-sidebar-reviews-upward-reviews.component.scss']
})
export class InformationSidebarReviewsUpwardReviewsComponent implements OnInit {

  public readonly eEvaluationFeedbackRequestState = EvaluationFeedbackRequestState;
  public readonly eEvaluationModuleType = EvaluationModuleType;
  public readonly eSurveyQuestionType = SurveyQuestionType;
  public readonly eCommonMessages = CommonMessages;

  @Input() cycle: EvaluationCycle;
  @Input() userIds: number[];
  @Input() userIdsManaging: number[];
  @Input() assessmentGroupsFiltered: AssessmentGroupInfoFiltered;
  
  state: IState;
  upwardReviews: EvaluationFeedbackRequest[];
  summary: ReviewStepSummary;
  usersManager: UserMinimal;

  constructor(
    public globals: Globals,
    private evaluationCycleAPIService: EvaluationCycleAPIService,
    private userAPIService: UserAPIService
  ) {
    this.cycle = undefined;
    this.userIds = [];
    this.userIdsManaging = [];

    this.state = {
      loading: true,
      error: false,
      errorMessage: ''
    };

    this.upwardReviews = [];
  }

  ngOnInit(): void {
    this.getData();
  }

  doError(message: string): void {
    this.state.loading = false;
    this.state.error = true;
    this.state.errorMessage = message;
  }

  getData(): void {
    if (!this.cycle || !this.userIds || this.userIds.length === 0) {
      this.doError('No data');
      return;
    }

    const upwardReviewStep = this.cycle.modules.find(step => step.evaluationModuleType === EvaluationModuleType.UPWARD_REVIEW);
    if (!upwardReviewStep) {
      this.doError('No upward review step found');
      return;
    }

    this.handleReviewSubjectAccessType(upwardReviewStep.reviewSubjectAccessType);
  }

  private handleReviewSubjectAccessType(reviewSubjectAccessType: ReviewSubjectAccessType): void {
    switch (reviewSubjectAccessType) {
      case ReviewSubjectAccessType.LEGACY:
      case ReviewSubjectAccessType.SUBJECT_ACCESS:
        this.getAllRequests();
        break;
      case ReviewSubjectAccessType.MANAGER_APPROVAL:
        this.getSharedRequests();
        break;
      case ReviewSubjectAccessType.MANAGER_SUMMARY:
        this.getSummaryData();
        break;
    }
  }

  getAllRequests(): void {
    firstValueFrom(
      this.evaluationCycleAPIService.getEvaluationUserByCycleIdAndUserId(this.cycle.id, this.userIds[0])
    )
      .then(evaluationUser => {
        let upwardReviews = evaluationUser.requestsAboutUser.filter(request =>
          request.type === EvaluationFeedbackRequestType.UPWARD_REVIEW &&
          [EvaluationFeedbackRequestState.APPROVED, EvaluationFeedbackRequestState.COMPLETED, EvaluationFeedbackRequestState.PENDING_PEER].includes(request.state)
        );
        if (upwardReviews.length === 0) {
          this.doError('No upward reviews found');
        }
        upwardReviews = this.sortRequests(upwardReviews);
        this.upwardReviews = upwardReviews;
        this.state.loading = false;
      });
  }

  getSharedRequests(): void {
    firstValueFrom(
      this.evaluationCycleAPIService.getUpwardReviewsCompletedSharedForUserId(this.cycle.id, this.userIds[0])
    )
      .then(feedbackRequests => {
        if (feedbackRequests.length === 0) {
          this.doError('No upward reviews found');
        }
        feedbackRequests = this.sortRequests(feedbackRequests);
        this.upwardReviews = feedbackRequests;
        this.state.loading = false;
      });
  }

  getSummaryData(): void {
    firstValueFrom(
      forkJoin([
        this.evaluationCycleAPIService.getUpwardReviewSummaryForUserInCycle(this.cycle.id, this.userIds[0]),
        this.userAPIService.getManagerForUser(this.userIds[0])
      ])
    )
      .then(([summary, usersManager]) => {
        if (!summary) {
          this.doError('Summary not submitted');
          return;
        }
        this.summary = summary;
        this.usersManager = usersManager;
        this.state.loading = false;
      });
  }

  sortRequests(upwardReviews: EvaluationFeedbackRequest[]): EvaluationFeedbackRequest[] {
    // Sort by status, completed at the top
    return upwardReviews.sort((a, b) => {
      if (a.state === EvaluationFeedbackRequestState.COMPLETED && b.state !== EvaluationFeedbackRequestState.COMPLETED) {
        return -1;
      } else if (a.state !== EvaluationFeedbackRequestState.COMPLETED && b.state === EvaluationFeedbackRequestState.COMPLETED) {
        return 1;
      } else {
        return 0;
      }
    });
  }

}
